import moment from "moment";

import { SPEC_FLAGS_MAP } from "../../../../Common/constants/SpecFlagConstants";

import { convertUtcToLocal } from "../../../../Common/utils/dateUtils";

export const MAX_RETESTS_ALLOWED = 1;

export const formatPendingRetestBannerDate = (dateValue) => {
  if (!dateValue) return "";
  return moment(new Date(convertUtcToLocal(dateValue))).format("MM/DD/YYYY");
};

/**
 * Sum the sample_size of the given tests, shown in ml
 * @param {Array} tests test objects from report_result
 * @returns {String} total sample size (e.g. "150ml"), or "" when no size is available
 */
export const getTotalSampleSize = (tests = []) => {
  let total = 0;
  tests.forEach((item) => {
    const value = parseFloat((item?.sample_size ?? "").toString().trim());
    if (!Number.isNaN(value)) total += value;
  });
  return total ? `${Number(total.toFixed(2))}ml` : "";
};

export const VALID_SPECS_SET = new Set(["0", "1", "2", "3", "4", "5", "6", "7", "8"]);

/**
 * True when the sample/report payload indicates Sample ID edit history exists.
 * @param {Object} sampleOrReport
 * @returns {boolean}
 */
export const hasSampleIdEditHistory = (sampleOrReport) => Boolean(
  sampleOrReport?.has_sample_id_edit_history,
);

/**
 * True when the sample/report payload indicates unit conversion history exists.
 * @param {Object} sampleOrReport
 * @returns {boolean}
 */
export const hasUnitConversionHistory = (sampleOrReport) => Boolean(
  sampleOrReport?.has_unit_conversion_history,
);

/**
 * @param {Object} params
 * @returns {boolean}
 */
export const shouldConvertSpecForUnitConversion = ({
  testCategory,
  currentSpec,
  specUnit,
  hasTestDetail,
  testDetailType,
}) => {
  if (testCategory === "MRL") {
    return false;
  }

  if (hasTestDetail && testDetailType && testDetailType !== "panel") {
    return false;
  }

  const specText = String(currentSpec ?? "").trim();
  if (!specText || specText === "-") {
    return false;
  }

  const specUnitText = String(specUnit).trim();
  if (!specUnitText || specUnitText === "-") {
    return false;
  }

  return true;
};

/** True when a non-MRL spec exists but spec_unit is "-". */
export const isUnitConversionUnavailableForMissingSpecUnit = ({
  currentSpec,
  specUnit,
  testCategory,
} = {}) => {
  if (testCategory === "MRL") {
    return false;
  }
  const specText = String(currentSpec ?? "").trim();
  const specUnitText = String(specUnit ?? "").trim();
  return specUnitText === "-" && specText !== "-";
};

/**
 * Latest edited Sample ID only (never the live sample_id).
 * Preference: latest_edited_sample_id → sample_id_edit_history[0].new_sample_id
 * @param {Object} sampleOrReport
 * @returns {string}
 */
export const getPreferredEditedSampleId = (sampleOrReport) => {
  if (!sampleOrReport) {
    return "";
  }

  if (sampleOrReport.latest_edited_sample_id) {
    return sampleOrReport.latest_edited_sample_id;
  }

  const sampleIdEditHistory = sampleOrReport.sample_id_edit_history;
  if (sampleIdEditHistory?.length) {
    return sampleIdEditHistory[0].new_sample_id;
  }

  return "";
};

/**
 * Sample ID shown in UI. Live sample_id is identity; edited string is display-only.
 * @param {Object} sampleOrReport
 * @returns {string}
 */
export const resolveDisplaySampleId = (sampleOrReport) => {
  if (!sampleOrReport) {
    return "";
  }

  return getPreferredEditedSampleId(sampleOrReport) || sampleOrReport.sample_id;
};

/**
 * True when the sample/report payload indicates Sample Type edit history exists.
 * @param {Object} sampleOrReport
 * @returns {boolean}
 */
export const hasSampleTypeEditHistory = (sampleOrReport) => Boolean(
  sampleOrReport?.has_sample_type_edit_history,
);

/**
 * Latest edited Sample Type only (never the live sample_type).
 * Preference: latest_edited_sample_type → sample_type_edit_history[0].new_sample_type
 * @param {Object} sampleOrReport
 * @returns {string}
 */
export const getPreferredEditedSampleType = (sampleOrReport) => {
  if (!sampleOrReport) {
    return "";
  }

  if (sampleOrReport.latest_edited_sample_type) {
    return sampleOrReport.latest_edited_sample_type;
  }

  const sampleTypeEditHistory = sampleOrReport.sample_type_edit_history;
  if (sampleTypeEditHistory?.length) {
    return sampleTypeEditHistory[0].new_sample_type;
  }

  return "";
};

/**
 * Sample Type shown in UI. Live sample_type is identity; edited string is display-only.
 * @param {Object} sampleOrReport
 * @returns {string}
 */
export const resolveDisplaySampleType = (sampleOrReport) => {
  if (!sampleOrReport) {
    return "";
  }

  return getPreferredEditedSampleType(sampleOrReport) || sampleOrReport.sample_type;
};

/**
 * True when edited_sample_id_path has at least one file path.
 * @param {string[]|*} editedSampleIdPath
 * @returns {boolean}
 */
export const hasEditedSampleIdPathFiles = (editedSampleIdPath) => (
  Array.isArray(editedSampleIdPath) && editedSampleIdPath.some(Boolean)
);

/**
 * Normalize flat edited_sample_id_path string[] into ReportPreviewModal's
 * nested filePaths shape: { Edited: { coa: [paths] } }.
 * @param {string[]} editedSampleIdPath
 * @returns {Object|null}
 */
export const normalizeEditedSampleIdPathsForPreview = (editedSampleIdPath) => {
  if (!hasEditedSampleIdPathFiles(editedSampleIdPath)) {
    return null;
  }

  return {
    Edited: {
      coa: editedSampleIdPath.filter(Boolean),
    },
  };
};

/**
 * Build CoaPreviewModal filePaths shape from a single S3 path.
 * @param {string} filePath
 * @returns {Object|null}
 */
export const buildSingleCoaPreviewFilePaths = (filePath) => {
  if (!filePath) {
    return null;
  }

  return {
    Preview: {
      coa: [filePath],
    },
  };
};

/**
 * Resolve COA paths for modal Preview/Download — edited PDF only (never official report_path).
 * Returns null when edited_sample_id_path has no files.
 * @param {Object} sampleOrReport
 * @returns {Object|null} filePaths shape for ReportPreviewModal / download
 */
export const resolveModalCoaFilePaths = (sampleOrReport) => {
  if (!hasEditedSampleIdPathFiles(sampleOrReport?.edited_sample_id_path)) {
    return null;
  }

  return normalizeEditedSampleIdPathsForPreview(sampleOrReport.edited_sample_id_path);
};

/**
 * Flatten nested or date-keyed COA path maps into a unique list of S3 paths.
 * @param {Object} filePaths
 * @returns {string[]}
 */
export const flattenCoaFilePaths = (filePaths) => {
  if (!filePaths || typeof filePaths !== "object") {
    return [];
  }

  const collectedPaths = [];

  Object.values(filePaths).forEach((reportTypes) => {
    if (!reportTypes || typeof reportTypes !== "object") {
      return;
    }
    Object.values(reportTypes).forEach((fileList) => {
      if (!Array.isArray(fileList)) {
        return;
      }
      fileList.forEach((filePath) => {
        if (filePath) {
          collectedPaths.push(filePath);
        }
      });
    });
  });

  return [...new Set(collectedPaths)];
};

/**
 * Latest test result row per test from /reportspecs/ report_result (same logic as COA row select).
 * @param {Object} spec - getSpec response.spec
 * @returns {Array}
 */
export const getLatestTestResultsFromReportSpec = (spec) => {
  if (!spec?.report_result) {
    return [];
  }

  const specData = Object.entries(spec.report_result);
  const dataSorted = specData.sort((a, b) => Date.parse(a[0]) - Date.parse(b[0]));

  if (dataSorted.length > 1) {
    const seen = new Set();
    const latestTestResults = {};
    dataSorted.forEach(([, testResults]) => {
      testResults.forEach((testObj) => {
        const testName = testObj.test;
        if (!seen.has(testName) && testObj.test_spec_flag !== "0") {
          if (!latestTestResults[testName]) {
            latestTestResults[testName] = testObj;
          }
          seen.add(testName);
        }
      });
    });
    return Object.values(latestTestResults);
  }

  if (dataSorted[0]?.[1]) {
    return dataSorted[0][1].filter((testObj) => testObj.test_spec_flag !== "0");
  }

  return [];
};

/**
 * Test results for spec hover tooltip — all tests from every report_result date,
 * newest date first (same data as SampleResultsModal across all date slides).
 * @param {Object} spec - getSpec / getEnvSpec response.spec
 * @returns {Array}
 */
export const getTestResultsForSpecTooltip = (spec) => {
  if (!spec?.report_result) {
    return [];
  }

  const sortedEntries = Object.entries(spec.report_result).sort(
    (a, b) => Date.parse(b[0]) - Date.parse(a[0]),
  );

  return sortedEntries.flatMap(([, testResults]) => testResults);
};

/* True when GET /reportspecs/ shows final results have been sent.
 * 1. Overall sample specs_flag is not In Process / In Process (Retest)
 * 2. Every report_result date has files in report_path
 * 3. No test is Due (flag "0" or test_result like "Due By")
 * 4. No test has a preliminary message
 * @param {Object} sampleResultsData
 * @returns {boolean}
 */
export const hasSentFinalResults = (sampleResultsData) => {
  const sampleInprocessStatuses = ["0", "4"];
  if (!sampleResultsData) {
    return false;
  }

  if (sampleInprocessStatuses.includes(sampleResultsData.specs_flag?.toString())) {
    return false;
  }

  const resultDates = Object.keys(sampleResultsData.report_result);
  const tests = Object.values(sampleResultsData.report_result).flat();

  if (resultDates.length === 0 || tests.length === 0) {
    return false;
  }

  const everyDateHasASentReport = resultDates.every((date) => {
    const filesByType = sampleResultsData.report_path[date];
    return filesByType && Object.values(filesByType).some((fileList) => fileList.length > 0);
  });

  if (!everyDateHasASentReport) {
    return false;
  }

  return tests.every((test) => {
    const isDue = test.test_spec_flag.toString() === "0" || test.test_result.startsWith("Due By");
    return !isDue && !test.message.trim();
  });
};

export const getColorForTestResult = (specList) => {
  const parsedList = [];

  (Array.isArray(specList) ? specList : []).forEach((element) => {
    if (!element) {
      return;
    }
    const parsedElement = {};
    const {
      test_result: result,
      test_spec: specs,
      test_unit: unit,
      spec_unit: specUnit,
      retest_sample_usid: retestSampleUsid,
      test,
      test_spec_flag,
      test_detail,
      test_category,
      message,
      is_under_review: isUnderReview,
      release_result_status: releaseResultStatus,
      contact_csm_requested_by: contactCsmRequestedBy,
      sample_size: sampleSize,
    } = element;
    let color;

    parsedElement.result = result ?? element.result;
    parsedElement.specs = specs ?? element.specs;
    parsedElement.unit = unit || element.unit || "";
    parsedElement.specUnit = specUnit || element.specUnit || "";
    parsedElement.retestSampleUsid = retestSampleUsid || "";
    parsedElement.test = test;
    parsedElement.detail = Boolean(test_detail || element.detailType);
    parsedElement.detailType = test_detail || element.detailType || "";
    parsedElement.test_spec_flag = test_spec_flag;
    parsedElement.test_category = test_category;
    parsedElement.message = message;
    parsedElement.is_under_review = isUnderReview;
    parsedElement.release_result_status = releaseResultStatus;
    parsedElement.contact_csm_requested_by = contactCsmRequestedBy;
    parsedElement.sample_size = sampleSize;

    if (element.test_spec_flag && element.test_spec_flag === "-") {
      color = SPEC_FLAGS_MAP.invalid;
      parsedElement.color = color;
    } else if (SPEC_FLAGS_MAP[element.test_spec_flag]) {
      color = SPEC_FLAGS_MAP[element.test_spec_flag].color;
      parsedElement.color = color;
    } else {
      parsedElement.color = SPEC_FLAGS_MAP.invalid?.color || element.color;
    }

    parsedList.push(parsedElement);
  });
  return parsedList;
};

export const getColorForPesticideResults = (compoundSpecs) => {
  const specData = {};

  Object.keys(compoundSpecs).forEach((country) => {
    const specs = compoundSpecs[country];
    specData[country] = specs.map((spec) => {
      spec.color = SPEC_FLAGS_MAP[spec.compound_spec] ? SPEC_FLAGS_MAP[spec.compound_spec].color : SPEC_FLAGS_MAP.invalid.color;
      return spec;
    });
  });

  return specData;
};

export const getCountriesSpecsFlagColors = (countriesSpecsFlags) => {
  const entries = Object.entries(countriesSpecsFlags).map(([code, specFlag]) => {
    let color;
    if (specFlag) {
      color = specFlag === "3" ? "#000000" : SPEC_FLAGS_MAP[specFlag].color;
    } else {
      color = SPEC_FLAGS_MAP.invalid.color;
    }
    return [code, color];
  });
  return new Map(entries);
};
